import EditTradeInput from "features/trades/EditTrade/EditTradeInput";
import { useContext, useState } from "react";
import { toFixed } from "@shared/utils/numbers";
import { EditSignalContext } from "features/signals/EditSignal/EditSignal.context";
import { getTargetsBelowPrice } from "features/signals/EditSignal/utils";
import { confirmWithCallback } from "utils/misc";
import useKeyPressWithCallback from "utils/hooks/useKeyPressWithCallback";
import Modal from "common/Modal/Modal";
import { OrderTypes } from "@shared/features/trades/common/types";
import { OrderStatus, LanguageNameSpaces } from "@shared/types";
import { isEditableTargetZone } from "@shared/features/signals/common/utils";
import {
  EditTargetData,
  EditTargetOrZone,
  EditTargetZone,
} from "@shared/features/signals/EditSignal/types";
import {
  getTargetPrice,
  isPartiallyFulfilledZone,
  moveZoneBorderToPriceReached,
  updateZoneFulfilledStatus,
} from "@shared/features/signals/EditSignal/utils";
import {
  getValidationMessage,
  orderHasStatus,
} from "@shared/features/trades/common/utils";
import { validateOrderRelativePrice } from "@shared/features/tradingTerminal/common/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface TargetModalProps {
  closeModal: () => void;
  title: string;
  onOk: (updatedTarget: EditTargetOrZone) => void;
  targetType: OrderTypes;
  target?: EditTargetOrZone;
}

export const TargetModal = ({
  closeModal,
  title,
  onOk,
  targetType,
  target,
}: TargetModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const {
    precisionConfig,
    currentPrice,
    signalData,
    entryTargets,
    cancelTarget,
    getTargetsByType,
    setEntryTargets,
  } = useContext(EditSignalContext)!;
  const isZone = target && isEditableTargetZone(target);
  const getDefaultRatio = () => {
    if (targetType === OrderTypes.STOP && !(target as EditTargetData)?.id)
      return "100";

    return target && !isZone && target.edit_ratio !== null
      ? toFixed(target?.edit_ratio * 100, ratioPrecision).toString()
      : "";
  };
  const getMinPrice = (): string => {
    let price: string | number;
    if (target) {
      price = getTargetPrice(target);
    } else {
      price = currentPrice;
    }

    return toFixed(price, pricePrecision).toString();
  };
  const ratioPrecision = (precisionConfig?.percent || 4) / 2;
  const getMaxPrice = (): string => {
    let price = "";
    if (isZone) {
      price = toFixed(target.edit_target_price[1], pricePrecision).toString();
    }
    return price;
  };
  const pricePrecision = precisionConfig?.price || 0;
  const [maxPrice, setMaxPrice] = useState(getMaxPrice);
  const [minPrice, setMinPrice] = useState(getMinPrice);
  const [confirmModal, confirmModalContextHolder] = Modal.useModal();
  const [ratio, setRatio] = useState<string>(getDefaultRatio);

  const maxPriceLimit = toFixed(
    signalData?.limits?.price.max || currentPrice * 10,
    pricePrecision,
    true
  );
  const minPriceLimit = toFixed(
    signalData?.limits?.price.min || currentPrice * 0.1,
    pricePrecision,
    true
  );
  const numberRatio = parseFloat(ratio);
  const ratioError = numberRatio < 1 || numberRatio > 100;
  const isShort = Boolean(signalData?.reverse);
  const partiallyFulfilledZone = target && isPartiallyFulfilledZone(target);

  const getPartiallyFulfilledDisabledMessage = (isMin: boolean) => {
    if (partiallyFulfilledZone) {
      if (isMin) {
        if (isShort) {
          return t("edit_signal.errors.cannot_edit_min_price_short", {
            price: target.price_reached,
          });
        }
      } else if (!isShort) {
        return t("edit_signal.errors.cannot_edit_max_price_long", {
          price: target.price_reached,
        });
      }
    }
  };

  const minPriceNumber = parseFloat(minPrice);
  const maxPriceNumber = parseFloat(maxPrice);
  const getFieldError = (isMin: boolean) => {
    if ((isMin && !minPrice) || (!isMin && !maxPrice)) return true;
    if (isMin)
      if (partiallyFulfilledZone && !isShort) {
        return (
          minPriceNumber > target.price_reached ||
          minPriceNumber <= minPriceLimit
        );
      } else
        return (
          minPriceNumber > maxPriceLimit || minPriceNumber <= minPriceLimit
        );
    else {
      if (partiallyFulfilledZone && isShort) {
        return (
          maxPriceNumber < target.price_reached ||
          maxPriceNumber >= maxPriceLimit
        );
      } else
        return (
          !!maxPrice &&
          (maxPriceNumber >= maxPriceLimit || maxPriceNumber <= minPriceNumber)
        );
    }
  };
  const minPriceError = getFieldError(true);
  const maxPriceError = getFieldError(false);

  const relativeToCurrentPriceErrors = validateOrderRelativePrice(
    isZone ? (isShort ? minPriceNumber : maxPriceNumber) : maxPriceNumber,
    targetType,
    isShort,
    !!signalData?.breakout,
    currentPrice,
    [],
    []
  );

  const getFieldValidationMessage = (isMin: boolean) => {
    if (isMin)
      if (partiallyFulfilledZone && !isShort) {
        return getValidationMessage(
          t("edit_signal.target_modal.min_price"),
          minPriceLimit,
          target.price_reached
        );
      } else
        return getValidationMessage(
          t("edit_signal.target_modal.min_price"),
          minPriceLimit,
          maxPriceLimit
        );
    else {
      if (partiallyFulfilledZone && isShort) {
        return getValidationMessage(
          t("edit_signal.target_modal.max_price"),
          target.price_reached,
          maxPriceLimit
        );
      } else
        return getValidationMessage(
          t("edit_signal.target_modal.max_price"),
          minPriceNumber,
          maxPriceLimit
        );
    }
  };

  const renderPriceInput = () =>
    isZone ? (
      <>
        <EditTradeInput
          mainValidationMessage={getFieldValidationMessage(true)}
          mainValidationMessageIsError={minPriceError}
          precision={pricePrecision}
          name={t("edit_signal.target_modal.min_price")}
          value={minPrice}
          onChange={setMinPrice}
          focusOnMount={true}
          isSignal={true}
          disabledMessage={getPartiallyFulfilledDisabledMessage(true)}
        />
        <EditTradeInput
          mainValidationMessage={getFieldValidationMessage(false)}
          mainValidationMessageIsError={maxPriceError}
          precision={pricePrecision}
          name={t("edit_signal.target_modal.max_price")}
          value={maxPrice}
          onChange={setMaxPrice}
          additionalErrors={relativeToCurrentPriceErrors}
          focusOnMount={true}
          isSignal={true}
          disabledMessage={getPartiallyFulfilledDisabledMessage(false)}
        />
      </>
    ) : (
      <EditTradeInput
        mainValidationMessage={getValidationMessage(
          t("edit_signal.target_modal.price"),
          minPriceLimit,
          maxPriceLimit
        )}
        mainValidationMessageIsError={minPriceError}
        precision={pricePrecision}
        name={t("edit_signal.target_modal.price")}
        value={minPrice}
        onChange={setMinPrice}
        additionalErrors={relativeToCurrentPriceErrors}
        focusOnMount={true}
        isSignal={true}
      />
    );
  const isFormInvalid = () => {
    if (partiallyFulfilledZone) {
      return isShort ? maxPriceError : minPriceError;
    } else if (isZone) {
      return minPriceError || maxPriceError;
    }

    return (
      minPriceError || ratioError || relativeToCurrentPriceErrors.length > 0
    );
  };

  const generateNewTarget = () => {
    const calculatedRatio = numberRatio / 100;
    const newTarget: EditTargetData = {
      id: 0,
      number: 0,
      ratio: NaN,
      edit_ratio: calculatedRatio || null,
      status: OrderStatus.PENDING,
      edit_status: OrderStatus.PENDING,
      target_price: "0",
      edit_target_price: minPrice,
      target_extra_data: undefined,
    };
    return newTarget;
  };

  const isError = isFormInvalid();

  const handleMoveStopPriceAboveTargetPrices = async (stopPrice: number) => {
    const targetsToCancel = getTargetsBelowPrice(
      entryTargets,
      stopPrice,
      isShort
    );

    if (targetsToCancel.length === 0) return Promise.resolve(true);
    const isMultipleTargetsToCancel = targetsToCancel.length > 1;

    const getCancelEntryConfirmMessage = () => {
      const direction = isShort
        ? t("edit_signal.target_modal.below")
        : t("edit_signal.target_modal.above");
      const entryType = isMultipleTargetsToCancel
        ? t("edit_signal.target_modal.entries")
        : t("edit_signal.target_modal.entry");
      const targetType = isMultipleTargetsToCancel
        ? t("edit_signal.target_modal.targets")
        : t("edit_signal.target_modal.target");
      return t("edit_signal.target_modal.cancel_entry_confirm", {
        direction,
        entryType,
        targetType,
      });
    };

    let allEntryTargets = getTargetsByType(OrderTypes.ENTRY);
    const okPressed = await confirmWithCallback(
      confirmModal,
      getCancelEntryConfirmMessage(),
      t("edit_signal.target_modal.warning"),
      false,
      t("edit_signal.target_modal.yes"),
      t("edit_signal.target_modal.no")
    );
    if (okPressed)
      targetsToCancel.forEach(
        (current) =>
          (allEntryTargets = cancelTarget(
            OrderTypes.ENTRY,
            entryTargets.indexOf(current),
            false,
            allEntryTargets
          ))
      );

    setEntryTargets(allEntryTargets);
    return okPressed;
  };

  const handleMoveStopPriceAboveZonePrice = async (
    entryZone: EditTargetZone,
    stopPrice: number
  ) => {
    const getCancelEntryZoneMessage = () => {
      const isZoneFulfilled = orderHasStatus(
        entryZone,
        true,
        OrderStatus.FULFILLED
      );
      if (isZoneFulfilled || !entryZone.price_reached) return;
      const stopPriceSurpassedPriceReached =
        entryZone.price_reached &&
        ((isShort && stopPrice <= entryZone.price_reached) ||
          (!isShort && stopPrice >= entryZone.price_reached));

      const direction = isShort
        ? t("edit_signal.target_modal.below")
        : t("edit_signal.target_modal.above");
      const zoneField = isShort
        ? t("edit_signal.target_modal.max")
        : t("edit_signal.target_modal.min");

      if (stopPriceSurpassedPriceReached) {
        return (
          <>
            {t("edit_signal.target_modal.zone_message_line1", {
              direction,
              zoneField,
            })}
            <br />
            {t("edit_signal.target_modal.zone_message_line2", {
              zoneField,
              priceReached: entryZone.price_reached,
            })}
            <br />
            <br />
            {t("edit_signal.target_modal.zone_message_line3")}
          </>
        );
      }
    };

    const cancelZoneMessage = getCancelEntryZoneMessage();
    if (!cancelZoneMessage) return Promise.resolve(true);

    const okPressed = await confirmWithCallback(
      confirmModal,
      cancelZoneMessage,
      t("edit_signal.target_modal.warning"),
      false,
      t("edit_signal.target_modal.yes"),
      t("edit_signal.target_modal.no")
    );
    if (okPressed) moveZoneBorderToPriceReached(entryZone, isShort);
  };

  const moveStopAboveEntry = () => {
    const targets = entryTargets.filter((target) =>
      orderHasStatus(
        target,
        false,
        OrderStatus.PENDING,
        OrderStatus.PARTIALLY_FULFILLED
      )
    );

    const stopPrice = minPriceNumber;
    if (isEditableTargetZone(targets[0])) {
      return handleMoveStopPriceAboveZonePrice(targets[0], stopPrice);
    }
    return handleMoveStopPriceAboveTargetPrices(stopPrice);
  };

  const onOkClick = async () => {
    if (isError) return;
    const calculatedRatio = numberRatio / 100;
    let currentTarget: EditTargetOrZone;
    if (targetType === OrderTypes.STOP) {
      const didMovePrice = await moveStopAboveEntry();
      if (!didMovePrice) return;
    }
    if (target) {
      currentTarget = { ...target };
      if (isEditableTargetZone(currentTarget)) {
        currentTarget.edit_target_price = [minPrice, maxPrice];
      } else {
        currentTarget.edit_target_price = minPrice;
        currentTarget.edit_ratio = isNaN(numberRatio) ? null : calculatedRatio;
      }
    } else {
      currentTarget = generateNewTarget();
    }
    updateZoneFulfilledStatus(currentTarget, isShort);
    onOk(currentTarget);
    closeModal();
  };
  useKeyPressWithCallback("Enter", onOkClick);

  const shouldShowRatioInput = !isZone && targetType !== OrderTypes.STOP;

  const getRatioOptionalFieldMessage = () => {
    const isOptional = (getTargetsByType(targetType) as EditTargetData[]).some(
      (current) => current.ratio == null
    );
    return isOptional ? t("edit_signal.target_modal.optional") : "";
  };

  return (
    <Modal
      open={true}
      destroyOnHidden={true}
      onCancel={closeModal}
      centered={true}
      closable={false}
      title={title}
      onOk={onOkClick}
      okButtonProps={{
        disabled: isError,
      }}
    >
      {renderPriceInput()}
      {shouldShowRatioInput && (
        <EditTradeInput
          mainValidationMessage={`${getRatioOptionalFieldMessage()}${getValidationMessage(
            t("edit_signal.target_modal.ratio"),
            1,
            100
          )}`}
          mainValidationMessageIsError={ratioError}
          precision={ratioPrecision}
          name={t("edit_signal.target_modal.ratio")}
          value={ratio}
          onChange={setRatio}
          inputPrefix={"%"}
        />
      )}
      {confirmModalContextHolder}
    </Modal>
  );
};
